import jsPDF from 'jspdf';
import { format } from 'date-fns';
import JunoButton from '../common/JunoButton';
import { useSelector } from 'react-redux';

const JncPaymentConfirmation = ({ data }) => {
    const whiteLabelLogo = sessionStorage.getItem('logo');
    const imageBaseURl = useSelector(state => state.config.image_base_url);

    const generatePDF = async paymentData => {
        const imageUrl = whiteLabelLogo || `${imageBaseURl}/landingpage/dark-logo.svg`;

        try {
            const response = await fetch(imageUrl);
            if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type');

            let dataUrl;
            if (contentType.includes('svg')) {
                const svgText = await response.text();
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);
                    dataUrl = canvas.toDataURL('image/png');
                    createPaymentConfirmationPDF(paymentData, dataUrl, img.width, img.height);
                };

                img.onerror = () => {
                    console.error('Failed to load SVG image into the canvas');
                };

                img.src = 'data:image/svg+xml;base64,' + btoa(svgText);
            } else {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.src = imageUrl;
                img.onload = () => {
                    createPaymentConfirmationPDF(paymentData, img, 50, 10);
                };
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const createPaymentConfirmationPDF = (paymentData, dataUrl, imgWidth, imgHeight) => {
        const doc = new jsPDF();

        const maxWidth = 50;
        const maxHeight = 10;

        let width = imgWidth;
        let height = imgHeight;

        const aspectRatio = width / height;
        if (width > maxWidth || height > maxHeight) {
            if (width / maxWidth > height / maxHeight) {
                width = maxWidth;
                height = maxWidth / aspectRatio;
            } else {
                height = maxHeight;
                width = maxHeight * aspectRatio;
            }
        }

        const textColor = [52, 64, 84];

        // Add title and date
        doc.setFontSize(15);
        doc.setFont('Inter', 'bold');
        doc.text('Payment confirmation', 10, 10);

        doc.setFontSize(11);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text(paymentData.date, 10, 18);

        // Add Transaction ID
        doc.setFont('Inter', 'bold');
        doc.text('Transaction ID', 10, 30);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text(paymentData.transactionId, 10, 36);

        // Add paid on behalf of section
        doc.setFont('Inter', 'bold');
        doc.text('Paid on behalf of:', 10, 45);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text('Customer name:', 10, 53);
        doc.text(paymentData.customerName, 50, 53);
        doc.text('Customer address:', 10, 61);
        doc.text(paymentData.customerAddress, 50, 61, { maxWidth: 150 });

        // Add Remitter Details section
        doc.setFont('Inter', 'bold');
        doc.text('Beneficiary Details', 10, 75);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text('Beneficiary name:', 10, 83);
        doc.text(paymentData.beneficiaryName, 50, 83);
        doc.text('Beneficiary address:', 10, 91);
        doc.text(paymentData.beneficiaryAddress, 50, 91, { maxWidth: 150 });

        // Add Beneficiary Bank Details section
        doc.setFont('Inter', 'bold');
        doc.text('Beneficiary Bank Details', 10, 105);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text('Bank name:', 10, 113);
        doc.text(paymentData.beneficiaryBankName, 50, 113);
        doc.text('Bank address:', 10, 121);
        doc.text(paymentData.beneficiaryBankAddress, 50, 121, { maxWidth: 150 });
        doc.text('IBAN:', 10, 129);
        doc.text(paymentData.iban, 50, 129);
        doc.text('SWIFT:', 10, 137);
        doc.text(paymentData.swift, 50, 137);
        doc.text('Account number:', 10, 145);
        doc.text(paymentData.accountNumber, 50, 145);
        doc.text('Sort code:', 10, 153);
        doc.text(paymentData.sortCode, 50, 153);
        doc.text('Additional info:', 10, 161);
        doc.text(paymentData.additionalInfo, 50, 161);

        // Add Payment section
        doc.setFont('Inter', 'bold');
        doc.text('Payment', 10, 175);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text('Currency:', 10, 183);
        doc.text(paymentData.currency, 50, 183);
        doc.text('Amount:', 10, 191);
        doc.text(paymentData.amount, 50, 191);

        // Add Reference section
        doc.setFont('Inter', 'bold');
        doc.text('Reference', 10, 205);
        doc.setFont('Inter', 'normal');
        doc.setTextColor(...textColor);
        doc.text(paymentData.reference, 10, 213, { maxWidth: 190 });

        // Add footer
        doc.addImage(dataUrl, 'PNG', 10, 260, width, height);
        doc.setDrawColor(208, 213, 221);
        doc.line(10, 270, 200, 270);
        doc.setFontSize(10);
        doc.setFont('Inter', 'bold');
        doc.setTextColor(29, 41, 57);
        doc.text(`© Junomoney ${new Date().getFullYear()} | All rights reserved`, 10, 275);
        doc.setTextColor(71, 84, 103);
        doc.setFont('Inter', 'normal');
        doc.text(
            `Juno Pay Ltd (trading as Juno Money) - (Company Number: 2024801421), with its registered office at Suite #229, 6030 88ST NW, Edmonton, Alberta, T6E6G4, Canada. Juno Pay Ltd is authorised by FINTRAC as a Money Service Business (MSB No. M23335654).`,
            10,
            280,
            { maxWidth: 190 },
        );

        doc.save('payment-confirmation.pdf');
    };

    const paymentData = {
        date: format(data.status.updated, 'dd.MM.yy hh:mm'),
        transactionId: data.transactionId?.toString() || '-',
        customerName: data.clientRef.name?.toString() || '-',
        customerAddress: data.clientRef.address?.toString() || '-',
        beneficiaryName: data.transactionDetails[0].beneficiaryName?.toString() || '-',
        beneficiaryAddress: data.transactionDetails[0].beneficiaryAddress?.toString() || '-',
        beneficiaryBankName: data.transactionDetails[0].bankName?.toString() || '-',
        beneficiaryBankAddress: data.transactionDetails[0].bankAddress?.toString() || '-',
        iban: data.transactionDetails[0].iban?.toString() || '-',
        swift: data.transactionDetails[0].swift?.toString() || '-',
        accountNumber: data.transactionDetails[0].beneficiaryAccountNumber?.toString() || '-',
        sortCode: data.transactionDetails[0].sortCode?.toString() || '-',
        additionalInfo: data.transactionDetails[0].additionalBankDetails?.toString() || '-',
        currency: data.transactionDetails[0].currency?.toString() || '-',
        amount: data.transactionDetails[0].amount?.toString() || '-',
        reference: data.transactionDetails[0].reference?.toString() || '-',
    };
    return (
        <>
            <div className="flex justify-between items-center">
                <div className="">
                    <p className="text-table-col-title text-xs uppercase font-semibold mb-1">
                        Payment confirmation
                    </p>
                    <p className="!text-text break-word">
                        Download Payment confirmation in PDF format.
                    </p>
                </div>
                <JunoButton
                    className="!w-28 !text-sm font-semibold text-text lg:flex hidden"
                    type="widget"
                    onClick={() => generatePDF(paymentData)}>
                    DOWNLOAD
                </JunoButton>
                <button
                    className="rounded-full p-2.5 bg-[#F4F4F5] dark:bg-[#FFFFFF14] lg:hidden block"
                    onClick={() => generatePDF(paymentData)}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path
                            d="M16.5 11.5V12.5C16.5 13.9001 16.5 14.6002 16.2275 15.135C15.9878 15.6054 15.6054 15.9878 15.135 16.2275C14.6002 16.5 13.9001 16.5 12.5 16.5H5.5C4.09987 16.5 3.3998 16.5 2.86502 16.2275C2.39462 15.9878 2.01217 15.6054 1.77248 15.135C1.5 14.6002 1.5 13.9001 1.5 12.5V11.5M13.1667 7.33333L9 11.5M9 11.5L4.83333 7.33333M9 11.5V1.5"
                            stroke="#344054"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="dark:stroke-white stroke-[#344054]"
                        />
                    </svg>
                </button>
            </div>
        </>
    );
};

export default JncPaymentConfirmation;
